import React, { useState, useEffect, useRef } from 'react';
import { 
  FileSignature, CheckCircle2, Clock, Eye, Download, Loader2,
  AlertTriangle, PenTool, X, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  sent: { label: 'Pendiente de firma', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  viewed: { label: 'En revisión', color: 'bg-[rgba(232,106,31,0.15)] text-[#E86A1F]', icon: Eye },
  signed: { label: 'Firmado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  expired: { label: 'Expirado', color: 'bg-red-500/20 text-red-400', icon: AlertTriangle }
};

export default function ContractsPortalTab() {
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signatureType, setSignatureType] = useState('typed'); // 'typed' or 'draw'
  const [typedName, setTypedName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/contracts/my-contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data.contracts || []);
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewContract = async (contract) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(
        `${API_URL}/api/contracts/my-contracts/${contract.contract_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedContract(response.data);
    } catch (error) {
      toast.error('Error al cargar contrato');
    }
  };

  const openSignModal = (contract) => {
    setSelectedContract(contract);
    setShowSignModal(true);
    setTypedName('');
    setAcceptedTerms(false);
  };

  const signContract = async () => {
    if (!acceptedTerms) {
      toast.error('Debes aceptar los términos');
      return;
    }

    let signatureData = '';
    
    if (signatureType === 'typed') {
      if (!typedName.trim()) {
        toast.error('Escribe tu nombre para firmar');
        return;
      }
      signatureData = typedName;
    } else {
      // Get canvas data
      const canvas = canvasRef.current;
      if (canvas) {
        signatureData = canvas.toDataURL('image/png');
      }
    }

    setSigning(true);
    try {
      const token = localStorage.getItem('portal_token');
      const user = JSON.parse(localStorage.getItem('portal_user') || '{}');
      
      await axios.post(
        `${API_URL}/api/contracts/my-contracts/${selectedContract.contract_id}/sign`,
        {
          signer_name: typedName || user.name || 'Cliente',
          signer_email: user.email || '',
          signature_data: signatureData,
          accepted_terms: acceptedTerms
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('¡Contrato firmado exitosamente!');
      setShowSignModal(false);
      setSelectedContract(null);
      loadContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al firmar contrato');
    } finally {
      setSigning(false);
    }
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const pendingContracts = contracts.filter(c => c.status !== 'signed');
  const signedContracts = contracts.filter(c => c.status === 'signed');

  return (
    <div className="max-w-3xl mx-auto" data-testid="contracts-portal">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl mb-4">
          <FileSignature className="w-8 h-8 text-[#1D1D1F]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D1D1F] mb-2">Mis Contratos</h2>
        <p className="text-[#AEAEB2]">
          Revisa y firma tus contratos de servicio
        </p>
      </div>

      {/* Pending Contracts */}
      {pendingContracts.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#E86A1F]" />
            Pendientes de firma ({pendingContracts.length})
          </h3>
          <div className="space-y-4">
            {pendingContracts.map((contract) => {
              const status = STATUS_CONFIG[contract.status] || STATUS_CONFIG.sent;
              const StatusIcon = status.icon;
              
              return (
                <div
                  key={contract.contract_id}
                  className="bg-white/5 rounded-xl p-5 border border-purple-500/30"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${status.color} rounded-full text-xs font-medium mb-2`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                      <h4 className="text-lg font-semibold text-[#1D1D1F]">{contract.title}</h4>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#AEAEB2]">
                        <span>${contract.monthly_amount?.toLocaleString()} MXN/mes</span>
                        <span>{contract.contract_duration_months} meses</span>
                        <span>Inicio: {contract.start_date}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => openSignModal(contract)}
                      className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-violet-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all"
                    >
                      <PenTool className="w-4 h-4" />
                      Firmar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Signed Contracts */}
      {signedContracts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Contratos firmados ({signedContracts.length})
          </h3>
          <div className="space-y-3">
            {signedContracts.map((contract) => (
              <div
                key={contract.contract_id}
                className="bg-white/5 rounded-xl p-4 border border-white/10"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-[#1D1D1F]">{contract.title}</h4>
                    <p className="text-sm text-[#AEAEB2] mt-1">
                      Firmado el {new Date(contract.signed_at).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    Firmado
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {contracts.length === 0 && (
        <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
          <FileSignature className="w-12 h-12 text-[#6E6E73] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">Sin contratos</h3>
          <p className="text-[#AEAEB2]">
            No tienes contratos pendientes ni firmados
          </p>
        </div>
      )}

      {/* Sign Modal */}
      {showSignModal && selectedContract && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1f] rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#1D1D1F]">Firmar Contrato</h3>
                <button
                  onClick={() => setShowSignModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-[#AEAEB2]" />
                </button>
              </div>

              {/* Contract Summary */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-[#1D1D1F] mb-2">{selectedContract.title}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#AEAEB2]">Monto mensual:</span>
                    <span className="text-[#1D1D1F] ml-2">${selectedContract.monthly_amount?.toLocaleString()} MXN</span>
                  </div>
                  <div>
                    <span className="text-[#AEAEB2]">Duración:</span>
                    <span className="text-[#1D1D1F] ml-2">{selectedContract.contract_duration_months} meses</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[#AEAEB2]">Inicio:</span>
                    <span className="text-[#1D1D1F] ml-2">{selectedContract.start_date}</span>
                  </div>
                </div>
              </div>

              {/* Signature Type Selection */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setSignatureType('typed')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    signatureType === 'typed' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-white/10 text-[#AEAEB2] hover:bg-white/20'
                  }`}
                >
                  Escribir nombre
                </button>
                <button
                  onClick={() => setSignatureType('draw')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    signatureType === 'draw' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-white/10 text-[#AEAEB2] hover:bg-white/20'
                  }`}
                >
                  Dibujar firma
                </button>
              </div>

              {/* Signature Input */}
              {signatureType === 'typed' ? (
                <div className="mb-6">
                  <label className="block text-sm text-[#AEAEB2] mb-2">Tu nombre completo</label>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Escribe tu nombre completo"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-[#1D1D1F] placeholder-[#AEAEB2] focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {typedName && (
                    <div className="mt-3 p-4 bg-white/5 rounded-xl">
                      <p className="text-sm text-[#AEAEB2] mb-1">Vista previa de firma:</p>
                      <p className="text-2xl text-[#1D1D1F] italic" style={{ fontFamily: 'cursive' }}>
                        {typedName}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-[#AEAEB2]">Dibuja tu firma</label>
                    <button
                      onClick={clearCanvas}
                      className="text-sm text-purple-400 hover:text-purple-300"
                    >
                      Limpiar
                    </button>
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    className="w-full bg-white/10 rounded-xl border border-white/20 cursor-crosshair"
                  />
                </div>
              )}

              {/* Terms Acceptance */}
              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="w-5 h-5 rounded border-[rgba(0,0,0,0.08)] bg-white/10 text-purple-500 focus:ring-purple-500"
                  />
                </div>
                <span className="text-sm text-[#AEAEB2]">
                  He leído y acepto los términos y condiciones del contrato. 
                  Entiendo que esta firma digital tiene validez legal.
                </span>
              </label>

              {/* Sign Button */}
              <button
                onClick={signContract}
                disabled={signing || !acceptedTerms}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-violet-500 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Firmando...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Firmar Contrato
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
